import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, DataGrid, type DataGridColumn } from '@kss/ui';
import { Plus, RefreshCw } from 'lucide-react';

export interface ModuleListConfig<TRow> {
  /** Page title (already translated). */
  title: string;
  /** Route to the matching New form. */
  newPath: string;
  /** Add-button label. */
  newLabel: string;
  /** Optional service. If absent, the page shows an empty-state placeholder. */
  service?: { all: () => Promise<TRow[]> };
  /** Columns. Defaults to a generic id+name+status pattern. */
  columns?: DataGridColumn<TRow>[];
  /** Optional custom render for the empty-state body. */
  emptyState?: React.ReactNode;
}

const DEFAULT_COLUMNS: DataGridColumn<{
  id?: string;
  name?: string;
  status?: string;
}>[] = [
  { accessor: 'id' as never, title: '#', width: 80, sortable: true },
  { accessor: 'name' as never, title: 'Name', sortable: true, filterable: true },
  { accessor: 'status' as never, title: 'Status', sortable: true, width: 140 },
];

/**
 * Generic placeholder list page for modules whose backend service hasn't
 * been wired yet. Mirrors the company/employee list layout (toolbar +
 * DataGrid) so the look-and-feel is identical, but degrades gracefully
 * when the service or entity types are not yet available.
 */
export function ModulePlaceholderList<TRow extends object>({
  title,
  newPath,
  newLabel,
  service,
  columns,
  emptyState,
}: ModuleListConfig<TRow>) {
  const [rows, setRows] = useState<TRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    if (!service) return;
    setLoading(true);
    setErr(null);
    try {
      const data = await service.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  const cols = (columns ?? (DEFAULT_COLUMNS as unknown as DataGridColumn<TRow>[])) as DataGridColumn<TRow>[];

  return (
    <div className="-m-6 p-[3px]">
      {err && (
        <Alert variant="danger" className="mb-3">
          {err}
        </Alert>
      )}
      <DataGrid<TRow>
        title={<span className="hidden sm:inline">{title}</span>}
        data={rows}
        columns={cols}
        defaultPageSize={10}
        pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            {service && (
              <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            )}
            <Link to={newPath}>
              <Button size="sm">
                <Plus className="h-3.5 w-3.5" /> {newLabel}
              </Button>
            </Link>
          </div>
        }
      />
      {!service && (
        <div className="mt-6 rounded-md border border-dashed border-border bg-surface-card p-8 text-center">
          {emptyState ?? (
            <>
              <p className="text-sm font-semibold text-content-primary">No backend service yet</p>
              <p className="mt-1 text-xs text-content-secondary">
                A {title.toLowerCase()} CRUD endpoint isn't available on the live API. Use the form to draft entries; persistence will work once the backend is ready.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ModulePlaceholderList;
